import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * Router-agnostic unsaved-changes guard.
 *
 * Intentionally NO react-router `useBlocker` — that hook requires a data
 * router (createBrowserRouter) and crashed the page under the current
 * `<BrowserRouter>` setup. This hotfix restores rendering with a
 * pure-browser guard:
 *
 *   1. `beforeunload`  → browser refresh / tab-close prompt (native UI).
 *   2. `popstate`      → browser Back button; falls back to a
 *      `window.confirm(...)` prompt because we cannot render a React
 *      modal synchronously in response to a popstate event.
 *   3. `guardedNavigate(fn)` → in-page nav handlers (e.g. the Back
 *      arrow) call this instead of `navigate(...)` directly. When
 *      `dirty=true` it opens the parent-owned React modal; otherwise it
 *      runs the passed function unchanged.
 *
 * When `dirty === false` the hook is a complete no-op (initial render is
 * unaffected, no listeners attached).
 */
export function useUnsavedChangesGuard(dirty) {
  const [pendingNav, setPendingNav] = useState(null);
  // Set while we run the pending function so nested calls don't re-open
  // the modal (e.g. Save-as-Draft → navigate).
  const bypassRef = useRef(false);
  const dirtyRef = useRef(dirty);
  useEffect(() => { dirtyRef.current = dirty; }, [dirty]);

  // 1) Native beforeunload — browser owns the message text.
  useEffect(() => {
    if (!dirty) return undefined;
    const beforeUnload = (e) => {
      if (!dirtyRef.current) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, [dirty]);

  // 2) Browser Back / Forward — popstate fallback. We can't render a
  // React modal synchronously here (the URL has already changed), so we
  // use window.confirm and pushState the URL back on cancel.
  useEffect(() => {
    if (!dirty) return undefined;
    // Anchor: current URL at the time the form became dirty. We compare
    // against this to detect the back-button vs a normal navigation.
    const anchor = window.location.href;
    const onPopState = () => {
      if (!dirtyRef.current || bypassRef.current) return;
      const proceed = window.confirm(
        "You have unsaved changes to this fund. Leave without saving?"
      );
      if (!proceed) {
        // Restore the URL so the user stays on the form.
        window.history.pushState(null, "", anchor);
      } else {
        bypassRef.current = true;
      }
    };
    // Push an extra history entry so the first Back click lands here
    // (and popstate fires) instead of leaving the SPA silently.
    window.history.pushState(null, "", anchor);
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [dirty]);

  const helpers = useMemo(() => ({
    /** Try to navigate; if dirty, show the parent-owned modal instead. */
    guardedNavigate: (fn) => {
      if (!dirtyRef.current || bypassRef.current) { fn(); return; }
      setPendingNav({ run: fn });
    },
    /** Consumer called Save-as-Draft: skip the guard once and continue. */
    resumeAfterSave: () => {
      bypassRef.current = true;
      if (pendingNav?.run) pendingNav.run();
      setPendingNav(null);
      setTimeout(() => { bypassRef.current = false; }, 0);
    },
    /** Consumer chose Discard: still allow the navigation to complete. */
    resumeAfterDiscard: () => {
      bypassRef.current = true;
      if (pendingNav?.run) pendingNav.run();
      setPendingNav(null);
      setTimeout(() => { bypassRef.current = false; }, 0);
    },
    /** Consumer cancelled: close the modal, stay put. */
    cancel: () => { setPendingNav(null); },
  }), [pendingNav]);

  return { promptOpen: !!pendingNav, ...helpers };
}
